import React from 'react';
import ReactDOM from 'react-dom/client';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import App from './App';
import { isButtonEmbed } from './utils/embedOptions';
import { colors, radii, fonts, motion } from './theme/colors';
import './index.css';

// The design system defines a single light theme measured from the live
// reference site, so the app no longer follows `prefers-color-scheme`.
//
// Three brand rules are enforced here rather than per-component:
//   1. No shadows anywhere — `shadows` is flattened to 'none'.
//   2. Buttons are rounded rectangles at 10px, not pills. Only chips are pills.
//   3. Fills are flat: seal for primary, slate-teal for secondary. No gradients.
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: colors.accentPrimary, dark: colors.sealDark, light: colors.sealTint, contrastText: colors.textInverse },
    secondary: { main: colors.accentSecondary, dark: colors.teal900, light: colors.teal500, contrastText: colors.textInverse },
    success: { main: colors.success500, dark: colors.success700, light: colors.success100 },
    error: { main: colors.error500, dark: colors.error700, light: colors.error100 },
    warning: { main: colors.warning500, light: colors.warning100 },
    background: { default: colors.surfacePage, paper: colors.surface },
    text: { primary: colors.textPrimary, secondary: colors.textSecondary, disabled: colors.textMuted },
    divider: colors.borderSubtle,
  },
  // Every elevation level resolves to 'none' so no MUI surface can reintroduce
  // a shadow through `elevation`, `Menu`, `Dialog`, etc.
  shadows: Array(25).fill('none'),
  shape: { borderRadius: radii.card },
  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.1 },
    h2: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.1 },
    h3: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.15 },
    h4: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 },
    h5: { fontFamily: fonts.display, fontWeight: 600, lineHeight: 1.3 },
    h6: { fontFamily: fonts.display, fontWeight: 600, lineHeight: 1.3 },
    body1: { fontSize: '0.96875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.84375rem', lineHeight: 1.6 },
    overline: { fontFamily: fonts.mono, fontSize: '0.6875rem', letterSpacing: '0.06em', fontWeight: 400 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      // The button-only embed sits on the partner's page, so it must not paint
      // a background of its own.
      styleOverrides: {
        body: { backgroundColor: isButtonEmbed ? 'transparent' : colors.surfacePage },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radii.button,
          textTransform: 'none',
          padding: '13px 26px',
          transition: `background ${motion.durationFast} ${motion.easeStandard}, border-color ${motion.durationFast} ${motion.easeStandard}`,
          '&:active': { opacity: 0.9 },
        },
        sizeSmall: { padding: '10px 20px', fontSize: '0.84375rem' },
        sizeLarge: { padding: '15px 30px' },
        containedPrimary: {
          backgroundColor: colors.seal,
          '&:hover': { backgroundColor: colors.sealDark },
        },
        containedSecondary: {
          backgroundColor: colors.teal700,
          '&:hover': { backgroundColor: colors.teal900 },
        },
        outlined: {
          borderColor: colors.borderSubtle,
          color: colors.textSecondary,
          '&:hover': { borderColor: colors.borderEmphasis, backgroundColor: colors.powderTint },
        },
        text: { color: colors.textSecondary },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: colors.borderSubtle },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radii.card, border: `1px solid ${colors.borderSubtle}` },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radii.pill,
          fontFamily: fonts.mono,
          fontSize: '0.6875rem',
          letterSpacing: '0.02em',
          fontWeight: 400,
        },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.control,
          backgroundColor: colors.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderSubtle },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderEmphasis },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: radii.control } },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: radii.pill, backgroundColor: colors.powderTint },
        bar: { borderRadius: radii.pill },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: colors.borderSubtle } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { border: `1px solid ${colors.borderSubtle}`, borderRadius: radii.panel },
      },
    },
  },
});

// Button-only embed: shrink the document to the control and keep the URL out
// of search results (it is a fragment of the real page, not a page).
if (isButtonEmbed) {
  const clear = { background: 'transparent', margin: '0', padding: '0', overflow: 'hidden' };
  Object.assign(document.documentElement.style, clear);
  Object.assign(document.body.style, clear);
  const noindex = document.createElement('meta');
  noindex.name = 'robots';
  noindex.content = 'noindex, nofollow';
  document.head.appendChild(noindex);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);
